import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, message, Modal } from "antd";
import { CheckCircleFilled, WarningFilled } from "@ant-design/icons";
import { MatchResult } from "../../../../Entities/MatchResult";
import { openNotification } from "../../../../Components/Notification";

interface OnboardingMatchModalProps {
  profileMatchResult: MatchResult;
  open: boolean;
  onDismiss: () => void;
  jobPostId: string;
  onMoveToAssessment: () => Promise<void>;
  onSendRoundEmail: () => Promise<void>;
}

const OnboardingMatchModal: React.FC<OnboardingMatchModalProps> = ({
  profileMatchResult,
  open,
  onDismiss,
  jobPostId,
  onMoveToAssessment,
  onSendRoundEmail,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const ctaType = sessionStorage.getItem("screening_cta_type");
  const isQualified = profileMatchResult.status === "qualified";

  // Flow 1a: validate + qualified
  const isFlow1a = ctaType === "validate" && isQualified;
  // Flow 1b: validate + unqualified
  const isFlow1b = ctaType === "validate" && !isQualified;
  // Flow 2: save or generic
  const isFlow2 = !isFlow1a && !isFlow1b;

  const handleSendAssessmentInvite = async () => {
    try {
      setLoading(true);
      // First move to assessment, then send email
      await onMoveToAssessment();
      await onSendRoundEmail();
      message.success("The candidate will receive an email to start their assessment.");
     
      onDismiss();
    } catch (error: any) {
      console.error("Error sending assessment invite:", error);
      message.error("Failed to send assessment invite. Please try again.")
    } finally {
      setLoading(false);
    }
  };

  const handleViewJobPost = () => {
    navigate(`/job-posts/${jobPostId}`);
  };

  return (
    <Modal
      open={open}
      onCancel={onDismiss}
      footer={null}
      centered
      width={440}
      closable={true}
    >
      <div style={{ textAlign: "center", paddingTop: 16, paddingBottom: 16 }}>
        {/* Flow 1a: Assessment link sent (qualified) */}
        {isFlow1a && (
          <>
            <div style={{ marginBottom: 24 }}>
              <CheckCircleFilled
                style={{ fontSize: 48, color: "#16a34a", marginBottom: 12 }}
              />
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>
              Assessment link sent
            </h3>
            <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 24, lineHeight: 1.6 }}>
              An assessment link has been sent to the candidate's email, keep track
              of the candidates performance and application status from the console.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <Button type="primary" onClick={onDismiss} size="large">
                View Application
              </Button>
            </div>
          </>
        )}

        {/* Flow 1b: Action required (unqualified) */}
        {isFlow1b && (
          <>
            <div style={{ marginBottom: 24 }}>
              <WarningFilled
                style={{ fontSize: 48, color: "#f59e0b", marginBottom: 12 }}
              />
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>
              Action Required
            </h3>
            <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 24, lineHeight: 1.6 }}>
              Candidate does not match the requirements. However you can still send
              an assessment to get a detailed report.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <Button type="primary" onClick={handleSendAssessmentInvite} loading={loading} size="large">
                Send Assessment Invite
              </Button>
            </div>
          </>
        )}

        {/* Flow 2: Candidate processed successfully (save or generic) */}
        {isFlow2 && (
          <>
            <div style={{ marginBottom: 24 }}>
              <CheckCircleFilled
                style={{ fontSize: 48, color: "#16a34a", marginBottom: 12 }}
              />
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>
              Candidate processed successfully
            </h3>
            <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 24, lineHeight: 1.6 }}>
              A job post was created using the details you provided and the candidate
              was processed successfully. You can process more candidates by sharing
              the job post link to candidates or uploading resumes directly in the
              console.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <Button onClick={onDismiss} size="large">
                View Application
              </Button>
              <Button type="primary" onClick={handleViewJobPost} size="large">
                View Job Post
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default OnboardingMatchModal;
